import { api } from '@goaltree/backend/convex/_generated/api'
import {
  LEVEL_COLORS,
  LEVEL_IDS,
  type LevelId,
  weekHighlights,
} from '@goaltree/backend/convex/lib/productivity'
import { Dialog, DialogContent, DialogTitle } from '@goaltree/ui/components/dialog'
import { Drawer, DrawerContent, DrawerTitle } from '@goaltree/ui/components/drawer'
import { cn } from '@goaltree/ui/lib/utils'
import { useQuery } from 'convex/react'
import type { FunctionReturnType } from 'convex/server'
import { ChevronRight, RotateCcw, Share2, Star, Zap } from 'lucide-react'
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useTheme } from '@/components/theme-provider'
import { useIsMobile } from '@/hooks/use-mobile'
import { openShareCard } from '@/lib/share-card'

export type Productivity = NonNullable<FunctionReturnType<typeof api.productivity.get>>
type Win = Productivity['week']
type Mode = 'week' | 'day'

const card = 'flex flex-col gap-2.5 rounded-2xl border border-border/60 bg-card/40 p-4'
const eyebrow = 'text-xs font-semibold uppercase tracking-widest text-muted-foreground'

function useLevelColor() {
  const { resolvedTheme } = useTheme()
  const key = resolvedTheme === 'light' ? 'light' : 'dark'
  return (l: LevelId) => LEVEL_COLORS[l][key]
}

const weekdayLong = (lang: string, key: string) =>
  new Date(`${key}T12:00:00Z`).toLocaleDateString(lang, { weekday: 'long', timeZone: 'UTC' })
const weekdayNarrow = (lang: string, key: string) =>
  new Date(`${key}T12:00:00Z`).toLocaleDateString(lang, { weekday: 'narrow', timeZone: 'UTC' })
const rangeLabel = (lang: string, a: string, b: string) =>
  new Intl.DateTimeFormat(lang, { month: 'short', day: 'numeric', timeZone: 'UTC' }).formatRange(
    new Date(`${a}T12:00:00Z`),
    new Date(`${b}T12:00:00Z`),
  )

// "{{count}} points to {{level}}" with the level name in bold.
function PointsTo({ count, level }: { count: number; level: LevelId }) {
  const { t } = useTranslation()
  const mark = '\u0001'
  const [before, after] = t('productivity.pointsTo', { count, level: mark }).split(mark)
  return (
    <span className="text-[13px] text-muted-foreground">
      {before}
      <b className="font-bold text-foreground">{t(`productivity.levels.${level}`)}</b>
      {after}
    </span>
  )
}

function Gauge({
  score,
  level,
  color,
}: {
  score: number
  level: LevelId
  color: (l: LevelId) => string
}) {
  const id = useId()
  return (
    <div className="relative h-[140px] w-[240px]">
      <svg width="240" height="140" viewBox="0 0 240 140" aria-hidden className="overflow-visible">
        <defs>
          <linearGradient id={id} x1="20" y1="0" x2="220" y2="0" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor={color('steady')} />
            <stop offset="0.45" stopColor={color('veryProductive')} />
            <stop offset="0.8" stopColor={color('insane')} />
            <stop offset="1" stopColor={color('legendary')} />
          </linearGradient>
        </defs>
        <path
          d="M20 130 A100 100 0 0 1 220 130"
          fill="none"
          className="stroke-foreground/[0.08]"
          strokeWidth="16"
          strokeLinecap="round"
        />
        {score > 0 && (
          <>
            <path
              d="M20 130 A100 100 0 0 1 220 130"
              fill="none"
              stroke={`url(#${id})`}
              strokeWidth="16"
              strokeLinecap="round"
              pathLength={100}
              strokeDasharray={`${score} 200`}
              className="motion-safe:animate-[gauge-fill_1s_ease-out]"
            />
            <circle
              cx="20"
              cy="130"
              r="9"
              className="fill-background motion-safe:animate-[gauge-knob_1s_ease-out]"
              stroke={color(level)}
              strokeWidth="4"
              style={{ transformOrigin: '120px 130px', transform: `rotate(${score * 1.8}deg)` }}
            />
          </>
        )}
      </svg>
      <span className="absolute inset-x-0 bottom-0 text-center text-[56px] font-extrabold leading-none tracking-[-0.03em] tabular-nums text-foreground">
        {score}
      </span>
    </div>
  )
}

function Segments({ level, color }: { level: LevelId; color: (l: LevelId) => string }) {
  const cur = LEVEL_IDS.indexOf(level)
  return (
    <div className="flex w-full gap-1.5" aria-hidden>
      {LEVEL_IDS.map((l, i) => (
        <span
          key={l}
          className={cn('h-1.5 flex-1 rounded-full', i > cur && 'bg-foreground/[0.08]')}
          style={{
            ...(i <= cur && { backgroundColor: color(l) }),
            ...(i === cur && {
              boxShadow: `0 0 0 2px var(--background), 0 0 0 4px ${color(l)}`,
            }),
          }}
        />
      ))}
    </div>
  )
}

function Trend({ w, mode }: { w: Win; mode: Mode }) {
  const { t } = useTranslation()
  if (!w.level || !w.prevLevel) return null
  const d = LEVEL_IDS.indexOf(w.level) - LEVEL_IDS.indexOf(w.prevLevel)
  const level = t(`productivity.levels.${w.prevLevel}`)
  const sfx = mode === 'week' ? 'Week' : 'Day'
  return (
    <span className="text-sm text-muted-foreground">
      {d > 0
        ? t(`productivity.upFrom${sfx}`, { level })
        : d < 0
          ? t(`productivity.downFrom${sfx}`, { level })
          : t(`productivity.same${sfx}`)}
    </span>
  )
}

function NextLine({ w }: { w: Win }) {
  const { t } = useTranslation()
  if (!w.level) return null
  if (w.next?.level === 'legendary' && (w.legendaryLocked || w.level === 'insane'))
    return (
      <span className="flex items-center gap-1.5 text-center text-[13px] text-muted-foreground">
        <Star className="size-3.5 shrink-0 text-[#b87aff]" />
        {w.next.points > 0 ? (
          <>
            <PointsTo count={w.next.points} level="legendary" />
            <span aria-hidden>·</span>
          </>
        ) : null}
        {t('productivity.legendaryRule')}
      </span>
    )
  if (!w.next)
    return <span className="text-[13px] text-muted-foreground">{t('productivity.topLevel')}</span>
  return <PointsTo count={w.next.points} level={w.next.level} />
}

function Tiles({ w }: { w: Win }) {
  const { t } = useTranslation()
  const b = w.breakdown
  const tiles = [
    { v: String(b.tasksDone), l: t('productivity.tasksDone') },
    { v: b.habitsHitPct === null ? '–' : `${b.habitsHitPct}%`, l: t('productivity.habitsHit') },
    { v: String(b.urgentCleared), l: t('productivity.urgentCleared') },
    { v: `+${b.goalProgressPct}%`, l: t('productivity.goalProgress') },
  ]
  return (
    <div className="flex flex-col gap-2.5">
      <span className={eyebrow}>{t('productivity.builtIt')}</span>
      <div className="grid grid-cols-2 gap-2.5">
        {tiles.map((x) => (
          <div key={x.l} className="flex flex-col gap-1 rounded-2xl border border-border/60 p-3.5">
            <span className="font-mono text-[22px] font-bold tabular-nums text-foreground">
              {x.v}
            </span>
            <span className="text-[13px] text-muted-foreground">{x.l}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function ShareButton({ p, label }: { p: Productivity; label?: string }) {
  const { t } = useTranslation()
  if (p.week.score === null) return null
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation()
        openShareCard({ type: 'productivity', ref: p.weekStart })
      }}
      className="flex h-12 w-full cursor-pointer items-center justify-center gap-2 rounded-[14px] bg-foreground text-[15px] font-bold text-background transition-opacity hover:opacity-90"
    >
      <Share2 className="size-4" strokeWidth={2.5} />
      {label ?? t('productivity.share')}
    </button>
  )
}

export function ProductivityLevelCard({ data }: { data?: Productivity | null }) {
  const fetched = useQuery(api.productivity.get, data === undefined ? {} : 'skip')
  const p = data ?? fetched
  const { t } = useTranslation()
  const color = useLevelColor()
  const [mode, setMode] = useState<Mode>('week')
  const [open, setOpen] = useState(false)
  if (!p) return null
  const w = mode === 'week' ? p.week : p.day
  return (
    <section className={cn(card, 'gap-4')}>
      <div className="flex items-center justify-between gap-3">
        <span className={eyebrow}>{t('productivity.title')}</span>
        <div className="flex gap-0.5 rounded-full border border-border p-[3px]">
          {(['week', 'day'] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              aria-pressed={mode === m}
              className={cn(
                'cursor-pointer rounded-full px-3 py-0.5 text-xs font-semibold transition-colors',
                mode === m
                  ? 'bg-foreground text-background'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {t(m === 'week' ? 'productivity.thisWeek' : 'productivity.today')}
            </button>
          ))}
        </div>
      </div>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setOpen(true)
          }
        }}
        className="group flex cursor-pointer flex-col items-center gap-3.5 rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
      >
        {w.score === null || !w.level ? (
          <div className="flex flex-col items-center gap-1 py-6 text-center">
            <span className="text-2xl font-extrabold text-foreground">
              {t('productivity.nothingPlanned')}
            </span>
            <span className="max-w-xs text-sm text-muted-foreground">
              {t('productivity.nothingPlannedDesc')}
            </span>
          </div>
        ) : (
          <>
            <Gauge score={w.score} level={w.level} color={color} />
            <div className="flex flex-col items-center gap-1">
              <span
                className="text-[26px] font-extrabold tracking-[-0.02em]"
                style={{ color: color(w.level) }}
              >
                {t(`productivity.levels.${w.level}`)}
              </span>
              <Trend w={w} mode={mode} />
            </div>
            <Segments level={w.level} color={color} />
            <NextLine w={w} />
          </>
        )}
        <span className="flex items-center gap-0.5 text-xs font-semibold text-primary-text group-hover:underline">
          {t('productivity.seeWeek')}
          <ChevronRight className="size-3.5" />
        </span>
      </div>
      <Tiles w={w} />
      <ShareButton p={p} />
      <ProductivityWeekSheet p={p} open={open} onOpenChange={setOpen} />
    </section>
  )
}

export function ProductivityWeek({ p, title }: { p: Productivity; title?: React.ReactNode }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const color = useLevelColor()
  const w = p.week
  const h = weekHighlights(p.days)
  const shown = [...new Set(p.days.flatMap((d) => (d.level ? [d.level] : [])))].sort(
    (a, b) => LEVEL_IDS.indexOf(b) - LEVEL_IDS.indexOf(a),
  )
  const bestBits = h.best
    ? [
        h.best.tasksDone > 0 && t('productivity.tasksN', { count: h.best.tasksDone }),
        h.best.habitsAll && t('productivity.everyHabit'),
      ].filter(Boolean)
    : []
  return (
    <div className="flex flex-col gap-[18px]">
      <div className="flex items-center justify-between">
        {title ?? <span className={eyebrow}>{t('productivity.title')}</span>}
        <span className="font-mono text-xs text-muted-foreground">
          {rangeLabel(lang, p.weekStart, p.weekEnd)}
        </span>
      </div>
      {w.score === null || !w.level ? (
        <span className="text-2xl font-extrabold text-foreground">
          {t('productivity.nothingPlanned')}
        </span>
      ) : (
        <div className="flex items-baseline gap-2.5">
          <span className="text-[64px] font-extrabold leading-none tracking-[-0.04em] tabular-nums text-foreground">
            {w.score}
          </span>
          <div className="flex flex-col gap-0.5">
            <span className="text-lg font-bold" style={{ color: color(w.level) }}>
              {t(`productivity.levels.${w.level}`)}
            </span>
            <span className="text-[13px] text-muted-foreground">
              {p.best && p.bestCompared >= 3
                ? t('productivity.bestWeek', { count: p.bestCompared })
                : t('productivity.thisWeekScore')}
            </span>
          </div>
        </div>
      )}
      <div className="flex flex-col gap-2.5 rounded-[20px] border border-border/60 bg-card/40 px-4 pt-[18px] pb-3.5">
        <div className="flex h-[180px] items-end justify-between gap-2">
          {p.days.map((d, i) => {
            const c = d.level ? color(d.level) : undefined
            return (
              <div
                key={d.date}
                className="flex h-full flex-1 flex-col items-center justify-end gap-1.5"
              >
                <span className="font-mono text-[11px] tabular-nums text-foreground/80">
                  {d.score ?? (d.rest ? '' : '–')}
                </span>
                <div
                  className={cn(
                    'w-full rounded-lg motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-6 motion-safe:fill-mode-both motion-safe:duration-500',
                    !c &&
                      (d.future || d.rest
                        ? 'border border-dashed border-border'
                        : 'bg-foreground/[0.08]'),
                  )}
                  style={{
                    height:
                      d.score !== null
                        ? `${Math.max(4, d.score * 1.3)}px`
                        : d.rest || d.future
                          ? '24px'
                          : '4px',
                    animationDelay: `${i * 40}ms`,
                    ...(c && { backgroundColor: c }),
                    ...(c && d.level === 'insane' && { boxShadow: `0 0 14px ${c}88` }),
                  }}
                  title={d.rest ? t('productivity.rest') : undefined}
                />
                <span
                  className={cn(
                    'text-[11px]',
                    d.date === p.today ? 'font-bold text-foreground' : 'text-muted-foreground',
                  )}
                >
                  {weekdayNarrow(lang, d.date)}
                </span>
              </div>
            )
          })}
        </div>
        {shown.length > 0 && (
          <div className="flex flex-wrap gap-3 text-[11px] text-muted-foreground">
            {shown.map((l) => (
              <span key={l} className="flex items-center gap-1.5">
                <span className="size-2 rounded-full" style={{ backgroundColor: color(l) }} />
                {t(`productivity.levels.${l}`)}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2 text-sm">
        {h.best?.level && (
          <div className="flex items-center gap-2.5">
            <Zap className="size-[18px] shrink-0" style={{ color: color(h.best.level) }} />
            <span>
              <b className="text-foreground">
                {t('productivity.bestDay', {
                  day: weekdayLong(lang, h.best.date),
                  level: t(`productivity.levels.${h.best.level}`),
                })}
              </b>
              {bestBits.length > 0 && (
                <span className="text-muted-foreground"> · {bestBits.join(', ')}</span>
              )}
            </span>
          </div>
        )}
        {(h.rest || h.dip?.level) && (
          <div className="flex items-center gap-2.5 text-foreground/80">
            <RotateCcw className="size-[18px] shrink-0 text-muted-foreground" />
            <span>
              {h.rest
                ? t('productivity.restDay', { day: weekdayLong(lang, h.rest.date) })
                : t('productivity.dipped', {
                    day: weekdayLong(lang, h.dip!.date),
                    level: t(`productivity.levels.${h.dip!.level!}`),
                  })}
            </span>
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground/80">{t('productivity.fair')}</p>
      <ShareButton p={p} />
    </div>
  )
}

function ProductivityWeekSheet({
  p,
  open,
  onOpenChange,
}: {
  p: Productivity
  open: boolean
  onOpenChange: (o: boolean) => void
}) {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const Title = isMobile ? DrawerTitle : DialogTitle
  const body = (
    <ProductivityWeek p={p} title={<Title className={eyebrow}>{t('productivity.title')}</Title>} />
  )
  return isMobile ? (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="mx-auto w-full max-w-lg data-[vaul-drawer-direction=bottom]:max-h-[94dvh] data-[vaul-drawer-direction=bottom]:rounded-t-2xl">
        <div className="overflow-y-auto px-5 pt-4 pb-[max(env(safe-area-inset-bottom),20px)] text-foreground">
          {body}
        </div>
      </DrawerContent>
    </Drawer>
  ) : (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] max-w-md overflow-y-auto">{body}</DialogContent>
    </Dialog>
  )
}
